import { AdjacentDataMap, Object3DUserData, OrientationData, OriginalColors, initialOrientation } from "@utils/shape";
import { Injectable } from "@angular/core";
import { ComponentInteractionService } from "src/app/services/component-interaction/component-interaction.service";
import {
    FaceType,
    Face,
    CountInputData,
    CountOutputData,
    MaterialName,
} from "../../../../utils/count-calculator/count-calculator.model";
import { SceneManagerService } from "./scene-manager/scene-manager.service";
import { MeshType } from "~/src/utils/shape-type";
import { Mesh, MeshBasicMaterial } from "three";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { FaceMetaData, FaceMetaDataMap } from "~/src/utils/shape-facetype";

@Injectable({
    providedIn: "root",
})
export class CountCalculatorService {
    constructor(
        private componentInteractionService: ComponentInteractionService,
        private sceneManagerService: SceneManagerService,
        private snackBar: MatSnackBar,
    ) {}

    public async calculate(meshes: Mesh[]) {
        let scene = this.sceneManagerService.scene;
        for (let i = 0; i < meshes.length; i++) {
            let adjacentData = meshes[i].userData[Object3DUserData.adjacentData] as AdjacentDataMap;
            if (adjacentData && Object.keys(adjacentData).length > 0) {
                for (const faceKey of Object.keys(adjacentData) as Face[]) {
                    let faceUuid = adjacentData[faceKey];
                    const matchingObject = scene.getObjectByProperty("uuid", faceUuid) as Mesh;
                    if (matchingObject) {
                        this.updateFaceType(meshes[i], matchingObject, faceKey);
                    }
                }
            } else {
                this.handleAdjacentData(meshes[i]);
            }
        }

        this.connenctionAndFacePattern(meshes);
    }

    private updateFaceType(firstMesh: Mesh, secondMesh: Mesh, face: Face) {
        const firstObjOrientation = firstMesh.userData[Object3DUserData.orientationData];
        let secondObjOrientation = secondMesh.userData[Object3DUserData.orientationData];

        // count node or hole
        const countFaceTypes = (mesh: Mesh): { nodeCount: number; holeCount: number } => {
            let nodeCount = 0;
            let holeCount = 0;

            const faceTypeData = mesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap;

            // Iterate over the entries of shape.userData[Object3DUserData.faceType]
            for (const [_key, value] of Object.entries(faceTypeData)) {
                if (value.type === FaceType.Node) {
                    nodeCount++;
                } else if (value.type === FaceType.Hole) {
                    holeCount++;
                }
            }

            return { nodeCount, holeCount }; // Return both counts as an object
        };

        // call function to count node or hole
        const currentShapeNodeCount = countFaceTypes(firstMesh);

        // Check if the current shape has more than 3 Node or Hole faces
        const isShapeWithLimitedHolesAndNodes =
            currentShapeNodeCount.holeCount < 3 && currentShapeNodeCount.nodeCount < 3;

        const updateFace = (
            currentFace: Face,
            currentFaceMetaData: FaceMetaData,
            adjacentFace: Face,
            adjacentFaceMetaData: FaceMetaData,
        ) => {
            if (
                (adjacentFaceMetaData.type === FaceType.Plane && currentFaceMetaData.type === FaceType.Plane) ||
                (adjacentFaceMetaData.type === FaceType.HalfPlane &&
                    currentFaceMetaData.type === FaceType.HalfPlane &&
                    firstMesh.name === secondMesh.name &&
                    this.areOrientationsEqual(
                        firstMesh.userData[Object3DUserData.orientationData],
                        secondMesh.userData[Object3DUserData.orientationData],
                    ))
            ) {
                if (isShapeWithLimitedHolesAndNodes) {
                    firstMesh.userData[Object3DUserData.faceMetaDataMap][currentFace].type = FaceType.Node;
                    secondMesh.userData[Object3DUserData.faceMetaDataMap][adjacentFace].type = FaceType.Hole;
                }
            } else {
                firstMesh.userData[Object3DUserData.faceMetaDataMap][currentFace].type = handleFaceType(
                    currentFaceMetaData,
                    adjacentFaceMetaData,
                );
            }
        };

        function handleFaceType(currentFaceMetaData: FaceMetaData, adjacentFaceMetaData: FaceMetaData) {
            // Handle Node faces
            if (adjacentFaceMetaData.type === FaceType.Node) {
                const { type: currentType, originalType: currentOriginal } = currentFaceMetaData;
                const adjacentOriginal = adjacentFaceMetaData.originalType;

                switch (currentType) {
                    case FaceType.Curved:
                        return FaceType.Curved;

                    case FaceType.Plane:
                        return adjacentOriginal === FaceType.HalfPlane ? FaceType.Plane : FaceType.Hole;

                    case FaceType.HalfPlane:
                        return adjacentOriginal === FaceType.HalfPlane ? FaceType.Hole : FaceType.HalfPlane;

                    default:
                        return adjacentOriginal === currentOriginal ? FaceType.Hole : currentOriginal;
                }
            }

            // Handle Hole faces
            else if (adjacentFaceMetaData.type === FaceType.Hole) {
                const { type: currentType, originalType: currentOriginal } = currentFaceMetaData;
                const adjacentOriginal = adjacentFaceMetaData.originalType;

                switch (currentType) {
                    case FaceType.Curved:
                        return FaceType.Curved;

                    case FaceType.Plane:
                        return adjacentOriginal === FaceType.HalfPlane ? FaceType.Plane : FaceType.Node;

                    case FaceType.HalfPlane:
                        return adjacentOriginal === FaceType.HalfPlane ? FaceType.Node : FaceType.HalfPlane;

                    default:
                        return adjacentOriginal === currentOriginal ? FaceType.Node : currentOriginal;
                }
            } else if (adjacentFaceMetaData.type === FaceType.Curved) {
                return currentFaceMetaData.originalType;
            }

            // Handle For Plane
            else if (adjacentFaceMetaData.type === FaceType.Plane) {
                const { type: currentType, originalType: currentOriginal } = currentFaceMetaData;

                if (currentType === FaceType.Curved) {
                    return FaceType.Curved;
                }

                if (currentOriginal === FaceType.HalfPlane) {
                    return FaceType.HalfPlane;
                }

                if (currentShapeNodeCount.nodeCount < 3) {
                    return FaceType.Node;
                } else {
                    return FaceType.Hole;
                }
            }

            // Handle HalfPlane faces
            else if (adjacentFaceMetaData.type === FaceType.HalfPlane) {
                const { type: currentType, originalType: currentOriginal } = currentFaceMetaData;

                if (currentType === FaceType.Curved) {
                    return FaceType.Curved;
                }

                if (currentOriginal === FaceType.HalfPlane) {
                    if (currentShapeNodeCount.nodeCount < 3) {
                        return FaceType.Node;
                    } else {
                        return FaceType.Hole;
                    }
                }

                return FaceType.Plane;
            }

            return;
        }

        let mainFace: Face;
        let foundMeshFace: Face;

        switch (face) {
            case Face.Front:
                mainFace = firstObjOrientation[0];
                foundMeshFace = secondObjOrientation[2];
                break;
            case Face.Right:
                mainFace = firstObjOrientation[1];
                foundMeshFace = secondObjOrientation[3];
                break;
            case Face.Back:
                mainFace = firstObjOrientation[2];
                foundMeshFace = secondObjOrientation[0];
                break;
            case Face.Left:
                mainFace = firstObjOrientation[3];
                foundMeshFace = secondObjOrientation[1];
                break;
            case Face.Top:
                mainFace = firstObjOrientation[4];
                foundMeshFace = secondObjOrientation[5];
                break;
            case Face.Bottom:
                mainFace = firstObjOrientation[5];
                foundMeshFace = secondObjOrientation[4];
                break;
        }

        const currentFaceMetaData = firstMesh.userData[Object3DUserData.faceMetaDataMap][mainFace];
        const adjacentFaceMetaData = secondMesh.userData[Object3DUserData.faceMetaDataMap][foundMeshFace];

        // update the face type
        updateFace(mainFace, currentFaceMetaData, foundMeshFace, adjacentFaceMetaData);
        this.handleAdjacentData(firstMesh);
    }

    private areOrientationsEqual(firstOrientation: OrientationData, secondOrientation: OrientationData): boolean {
        return (
            firstOrientation.length === secondOrientation.length &&
            firstOrientation.every((value, index) => value === secondOrientation[index])
        );
    }

    private handleAdjacentData(firstMesh: Mesh) {
        const firstObjOrientation = firstMesh.userData[Object3DUserData.orientationData] as OrientationData;
        const initialOrientationData = initialOrientation;
        const adjacentData = firstMesh.userData[Object3DUserData.adjacentData] as AdjacentDataMap;
        const facesNotHavingAdjacentData = initialOrientationData.filter((face) => !adjacentData?.hasOwnProperty(face));

        const processFaceType = (faceIndex: number) => {
            const mainFace = firstObjOrientation[faceIndex];
            (firstMesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap)[mainFace].type = (
                firstMesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap
            )[mainFace].originalType;
        };

        if (facesNotHavingAdjacentData) {
            facesNotHavingAdjacentData.forEach((face) => {
                switch (face) {
                    case Face.Front:
                        processFaceType(0);
                        break;
                    case Face.Right:
                        processFaceType(1);
                        break;
                    case Face.Back:
                        processFaceType(2);
                        break;
                    case Face.Left:
                        processFaceType(3);
                        break;
                    case Face.Top:
                        processFaceType(4);
                        break;
                    case Face.Bottom:
                        processFaceType(5);
                        break;
                    default:
                        this.snackBar.open("Unknown Face Type", SnackBarConfig.Actions.OKAY, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                }
            });
        }
    }

    // create a connection data
    public connenctionAndFacePattern(meshes: Mesh[]) {
        let facePatterns = [];
        for (let i = 0; i < meshes.length; i++) {
            const mesh = meshes[i];
            // this face is world
            const connectionData = initialOrientation.map((face) => {
                let connectionType = (mesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap)[face].type;
                return { face: face, connectionType };
            });

            // Join connection types with "-" and ensure a trailing dash
            const facePatternData = connectionData
                .map((data) => (data.connectionType === FaceType.HalfPlane ? "HP" : data.connectionType[0]))
                .join(" - ");

            mesh.userData[Object3DUserData.facePattern] = facePatternData;
            mesh.userData[Object3DUserData.connectionData] = connectionData;

            const material = mesh.material || [];
            const originalColors: OriginalColors = mesh.userData[Object3DUserData.originalColors];
            const colorObject: Record<string, string> = {};

            // Populate colorObject with material name as key and original color as value
            (material as MeshBasicMaterial[]).forEach((material, index) => {
                if (material.name && MaterialName.includes(material.name)) {
                    // decimal number to hex
                    colorObject[material.name] = originalColors[index];
                }
            });

            let object = {
                name: mesh.name as MeshType,
                facePattern: mesh.userData[Object3DUserData.facePattern],
                color: colorObject, // Updated to include the material-color map
                connectionData: mesh.userData[Object3DUserData.connectionData],
                uuid: mesh.uuid,
            };

            facePatterns.push(object);
        }

        function transformInputToOutput(inputArray: CountInputData[]): CountOutputData[] {
            const outputMap = new Map<string, CountOutputData>();
            for (const item of inputArray) {
                const colorKey = JSON.stringify(item.color);
                const key = `${item.name}-${item.facePattern}-${colorKey}`;
                if (outputMap.has(key)) {
                    const existingItem = outputMap.get(key)!;
                    existingItem.count++;
                    (existingItem.uuid as any).push(item.uuid) as string[]; // Store UUIDs as an array
                } else {
                    // If the key doesn't exist, create a new entry
                    outputMap.set(key, {
                        name: item.name,
                        facePattern: item.facePattern,
                        color: item.color,
                        count: 1,
                        connectionData: item.connectionData,
                        uuid: [item.uuid] as unknown as string[], // Initialize UUID as an array
                    });
                }
            }

            // Convert the map values to an array
            const outputArray = Array.from(outputMap.values());
            return outputArray;
        }

        const outputPattern: CountOutputData[] = transformInputToOutput(facePatterns as []);
        this.componentInteractionService.setOutputPattern(outputPattern);
    }
}
